import { state } from './state.js';
import * as UI from './ui.js';
import { FUN_FACTS, MESSAGES } from './messages.js';
import { getOriginalSize } from './target_controller.js';
import { TOASTS } from './config/toasts.js';
import { BUILD_TOAST_PHASES, JOB_PHASES } from './config/jobPhases.js';
import { SLIDERS } from './config/sliders.js';
import { resolveDraftScale } from './draft_state.js';

let _buildToastShown = false;
let _buildToastEl = null;
let _buildToastPending = false;
let _completeToastShown = false;
let _jobStartScrollDone = false;
let _workflowSequenceBound = false;
let _heartbeatTimer = null;
let _lastStepIndex = null;
let _lastPhase = null;

const HEARTBEAT_MARKER_MS = 5000;
const STEP_COUNT = 4;

const STEP_META = {
    [JOB_PHASES.SCANNING]: {
        index: 1,
        label: 'Analyzing',
        desc: ({ file }) => {
            const raw = String(file || '').trim();
            if (!raw) return 'Checking library files...';
            if (raw === 'Checking cached library manifest...') return 'Checking cached library manifest...';
            if (raw === 'Loading cached tiles...' || raw === 'Loading cached tile previews...') {
                return 'Loading cached tile previews...';
            }
            if (
                raw === 'Processing new photos...'
                || raw === 'Scanning in progress...'
                || raw === 'Generating tile previews...'
            ) {
                return 'Generating tile previews...';
            }
            if (raw === 'Scanning files...') return 'Checking library files...';
            if (raw === 'Initializing library...') return 'Initializing library...';
            if (raw.startsWith('Loading feature index ')) return raw;
            const clean = UI.displayNameFromKey(raw);
            return clean ? `Generating tile previews: ${clean}...` : 'Generating tile previews...';
        }
    },
    [JOB_PHASES.ANALYZING]: {
        index: 1,
        label: 'Analyzing',
        desc: () => 'Analyzing tiles...'
    },
    [JOB_PHASES.PLANNING]: {
        index: 2,
        label: 'Planning',
        desc: ({ raw }) => {
            if (raw?.preview_generation_waiting) return 'Waiting for tile previews...';
            if (raw?.preview_generation_active) return 'Planning layout and generating tile previews...';
            return 'Planning layout...';
        }
    },
    [JOB_PHASES.RENDERING]: {
        index: 3,
        label: 'Rendering',
        desc: () => 'Rendering tiles...'
    },
    [JOB_PHASES.BUILDING]: {
        index: 4,
        label: 'Finalizing',
        desc: () => 'Finalizing mosaic...'
    }
};

function dismissBuildToast() {
    if (_buildToastEl) {
        try {
            if (typeof _buildToastEl.close === 'function') {
                _buildToastEl.close();
            }
        } catch (e) {}
        _buildToastEl = null;
    }
    _buildToastEl = null;
}

function markHeartbeat(bar) {
    if (!bar) return;
    bar.classList.add('heartbeat-live');
    if (_heartbeatTimer) clearTimeout(_heartbeatTimer);
    _heartbeatTimer = setTimeout(() => {
        bar.classList.remove('heartbeat-live');
        _heartbeatTimer = null;
    }, HEARTBEAT_MARKER_MS);
}

function clearHeartbeatMarkers() {
    if (_heartbeatTimer) {
        clearTimeout(_heartbeatTimer);
        _heartbeatTimer = null;
    }
    const bar = document.getElementById('progressBar');
    if (bar) bar.classList.remove('heartbeat-live');
}

function resolveScale(origW, origH) {
    return resolveDraftScale(origW, origH);
}

function getBuildToastMessage() {
    const libraries = Array.isArray(state.libraries) ? state.libraries : [];
    const lib = libraries.find((item) => item.id === state.selectedLibraryId) || null;
    const libCountRaw = Number(lib && lib.tile_count);
    const libCount = Number.isFinite(libCountRaw) && libCountRaw > 0
        ? libCountRaw.toLocaleString()
        : 'many';
    const libName = String((lib && lib.name) || 'your selected library');
    const { origW, origH } = getOriginalSize();
    const scale = resolveScale(origW, origH);
    const tileHeightDefault = SLIDERS.tileHeight.defaultValue;
    const tileHeight = parseInt(document.getElementById('thInput').value, 10) || tileHeightDefault;
    if (!origW || !origH || !scale) {
        return FUN_FACTS.BUILD_START_LONG({
            tileCount: 'many',
            libraryPhotoCount: libCount,
            libraryName: libName,
            remainingTileCount: 'many',
        });
    }
    const finalW = Math.round(origW * scale);
    const finalH = Math.round(origH * scale);
    const totalRows = Math.ceil(finalH / tileHeight);
    const totalCols = Math.ceil(finalW / tileHeight);
    const totalTiles = totalRows * totalCols;
    const remainingTiles = totalTiles > 1 ? totalTiles - 1 : totalTiles;
    if (totalTiles <= 2000) {
        return FUN_FACTS.BUILD_START_SHORT({
            tileCount: totalTiles.toLocaleString(),
            libraryPhotoCount: libCount,
            libraryName: libName,
        });
    }
    return FUN_FACTS.BUILD_START_LONG({
        tileCount: totalTiles.toLocaleString(),
        libraryPhotoCount: libCount,
        libraryName: libName,
        remainingTileCount: remainingTiles.toLocaleString(),
    });
}

function _maybeShowBuildToast() {
    if (_buildToastShown || !_buildToastPending || !_jobStartScrollDone) return;
    _buildToastPending = false;
    _buildToastShown = true;
    const toast = TOASTS.buildStarted(getBuildToastMessage());
    _buildToastEl = UI.showToast(toast.title, toast.message, toast.duration);
}

function _bindWorkflowSequence() {
    if (_workflowSequenceBound || typeof document === 'undefined') return;
    _workflowSequenceBound = true;
    document.addEventListener('workflow:sequence', (event) => {
        const detail = (event && event.detail) || {};
        const source = String(detail.source || '');
        const phase = String(detail.phase || '');
        if (source !== 'job_start') return;
        if (phase === 'scroll_start') {
            _jobStartScrollDone = false;
            return;
        }
        if (phase === 'scroll_end') {
            _jobStartScrollDone = true;
            _maybeShowBuildToast();
        }
    });
}

export function resetBuildToast() {
    _bindWorkflowSequence();
    _buildToastShown = false;
    _buildToastPending = false;
    _completeToastShown = false;
    _jobStartScrollDone = false;
    _lastStepIndex = null;
    _lastPhase = null;
    dismissBuildToast();
    clearHeartbeatMarkers();
}

export function applyProgressUpdate(detail = {}) {
    const { phase, pct, file, raw, suppressBuildToast } = detail;
    const progressArea = document.getElementById('progressArea');
    if (progressArea) progressArea.classList.remove('hidden');
    const bar = document.getElementById('progressBar');
    if (!bar) return;
    _bindWorkflowSequence();

    if (!_buildToastShown && !suppressBuildToast && pct > 0 && BUILD_TOAST_PHASES.has(phase)) {
        _buildToastPending = true;
        _maybeShowBuildToast();
    }

    const meta = STEP_META[phase];
    if (!meta) return;

    const stepLabel = document.getElementById('progressStepLabel');
    const stepPct = document.getElementById('progressStepPct');
    const stepDesc = document.getElementById('progressStepDesc');

    const pctValue = Number.isFinite(Number(pct)) ? Math.round(Number(pct)) : 0;
    if (stepLabel) stepLabel.innerText = `Step ${meta.index}/${STEP_COUNT}: ${meta.label}`;
    if (stepPct) stepPct.innerText = `${pctValue}%`;

    let desc = typeof meta.desc === 'function' ? meta.desc({ file, raw }) : String(meta.desc || '');
    if (_lastStepIndex !== null && meta.index !== _lastStepIndex) {
        desc = `Step ${_lastStepIndex}/${STEP_COUNT} complete. ${desc}`;
    }
    if (stepDesc) stepDesc.innerText = desc;

    if (_lastPhase && meta.index !== _lastStepIndex) {
        bar.style.width = '0%';
    }
    bar.className = `progress-fill ${meta.index === 1 ? 'active-scan' : 'active-build'}`;
    bar.style.width = `${pctValue}%`;
    markHeartbeat(bar);

    _lastStepIndex = meta.index;
    _lastPhase = phase;
}

export function applyCompleteProgressUi() {
    dismissBuildToast();
    clearHeartbeatMarkers();
    const progressArea = document.getElementById('progressArea');
    if (progressArea) progressArea.classList.remove('hidden');
    const bar = document.getElementById('progressBar');
    if (bar) {
        bar.style.width = '100%';
        bar.className = 'progress-fill active-build';
    }
    const stepLabel = document.getElementById('progressStepLabel');
    const stepPct = document.getElementById('progressStepPct');
    const stepDesc = document.getElementById('progressStepDesc');
    if (stepLabel) stepLabel.innerText = `Step ${STEP_COUNT}/${STEP_COUNT}: Complete`;
    if (stepPct) stepPct.innerText = '100%';
    if (stepDesc) stepDesc.innerText = 'Mosaic ready.';
}

export function showCompleteReadyToast() {
    if (_completeToastShown) return;
    _completeToastShown = true;
    UI.showToast(
        'The masterpiece is created!',
        'Mosaic is succesfully created. Enjoy your artwork!',
        10000,
    );
}

export function applyCancelledProgressUi() {
    dismissBuildToast();
    clearHeartbeatMarkers();
    const bar = document.getElementById('progressBar');
    if (bar) {
        bar.style.width = '0%';
        bar.className = 'progress-fill';
    }
    const stepLabel = document.getElementById('progressStepLabel');
    const stepPct = document.getElementById('progressStepPct');
    const stepDesc = document.getElementById('progressStepDesc');
    if (stepLabel) stepLabel.innerText = 'Cancelled';
    if (stepPct) stepPct.innerText = '0%';
    if (stepDesc) stepDesc.innerText = 'Job cancelled.';

    const toast = TOASTS.jobCancelled(MESSAGES.JOB_CANCELLED);
    UI.showToast(toast.title, toast.message, toast.duration);
}

export function applyErrorProgressUi() {
    dismissBuildToast();
    clearHeartbeatMarkers();
    const bar = document.getElementById('progressBar');
    if (bar) {
        bar.className = 'progress-fill';
    }
    const stepLabel = document.getElementById('progressStepLabel');
    const stepPct = document.getElementById('progressStepPct');
    const stepDesc = document.getElementById('progressStepDesc');
    if (stepLabel) stepLabel.innerText = 'Error';
    if (stepPct) stepPct.innerText = '0%';
    if (stepDesc) stepDesc.innerText = 'Job failed.';
}

_bindWorkflowSequence();
